import type { AppState, MonthlyCalc, YearlyCalc, CalcResult, Charge, FreelanceCharge, Revenu, Charges } from '../types';
import { getNotairesSousTraitanceMois } from './notairesEngine';
import { getExRanges, getTotalMonths } from './formatters';

function getExRangesFromState(state: AppState): [number, number][] {
  if (!state?.meta) return getExRanges();
  return getExRanges(state.meta.exercice1Duree, state.meta.exercice2Duree, state.meta.exercice3Duree);
}

function getMoisEx(state: AppState, mi: number): number {
  const ranges = getExRangesFromState(state);
  for (let i = 0; i < ranges.length; i++) {
    if (mi >= ranges[i][0] && mi <= ranges[i][1]) return i;
  }
  return ranges.length - 1;
}

/**
 * Détermine l'index d'exercice (0, 1, 2) pour un mois donné selon les ranges fournis.
 * Fallback : bornes standard [0-11, 12-23, 24-35].
 */
function getExerciceIndex(mi: number, exRanges?: [number, number][]): number {
  const ranges = exRanges ?? [[0, 11], [12, 23], [24, 35]];
  for (let i = 0; i < ranges.length; i++) {
    if (mi >= ranges[i][0] && mi <= ranges[i][1]) return i;
  }
  return ranges.length - 1;
}

/**
 * Résout le taux variable effectif pour le mois.
 * Priorité : `variablePctMensuels[mi]` > `variablePctExercices[ex]` > `variablePct` global.
 */
function resolveVariablePct(
  charge: {
    variablePct: number;
    variablePctExercices?: number[] | null;
    variablePctMensuels?: number[] | null;
  },
  mi: number,
  exRanges?: [number, number][],
): number {
  const perMonth = charge.variablePctMensuels;
  if (Array.isArray(perMonth) && perMonth.length > 0) {
    const v = perMonth[mi];
    if (Number.isFinite(v)) return v as number;
  }
  const perEx = charge.variablePctExercices;
  if (Array.isArray(perEx) && perEx.length > 0) {
    const ex = getExerciceIndex(mi, exRanges);
    const v = perEx[Math.min(ex, perEx.length - 1)];
    if (Number.isFinite(v)) return v as number;
  }
  return charge.variablePct ?? 0;
}

/**
 * Valeur mensuelle d'une charge pour le mois mi.
 * - Si indexée sur un revenu (variableRevenuId), retourne variablePct% de ce revenu.
 * - Si indexée sur une dépense (variableChargeId), retourne variablePct% du montant fixe de cette dépense.
 * - Sinon retourne le montant fixe (uniform / mensuel).
 *
 * `caScenarioFactor` (optionnel, défaut 1) : multiplicateur appliqué UNIQUEMENT à la part
 * indexée sur un revenu (variableRevenuId), afin que les charges variables sur le CA suivent
 * le curseur de scénario CA sans altérer les données sources.
 *
 * `exRanges` (optionnel) : bornes d'exercices pour résoudre `variablePctExercices` lorsqu'il
 * est défini. Par défaut [[0,11],[12,23],[24,35]].
 */
export function getChargeMonthValue(
  charge: Pick<Charge | FreelanceCharge, 'mode' | 'montantUniforme' | 'montantsMensuels' | 'variableRevenuId' | 'variableChargeId' | 'variablePct' | 'finMois'> & { variablePctExercices?: number[] | null; variablePctMensuels?: number[] | null },
  mi: number,
  revenus: Revenu[],
  allCharges?: Charges,
  caScenarioFactor: number = 1,
  exRanges?: [number, number][],
): number {
  if (charge.finMois != null && mi > charge.finMois) return 0;

  if (charge.variableRevenuId) {
    const pct = resolveVariablePct(charge, mi, exRanges);
    if (pct > 0) {
      const rev = revenus.find((r) => r.id === charge.variableRevenuId);
      if (rev) {
        const revVal =
          rev.mode === 'uniform'
            ? mi >= rev.debutMois ? rev.montantUniforme : 0
            : (rev.montantsMensuels[mi] ?? 0);
        return revVal * (pct / 100) * caScenarioFactor;
      }
    }
  }

  if (charge.variableChargeId && allCharges) {
    const pct = resolveVariablePct(charge, mi, exRanges);
    if (pct > 0) {
      const chargeKeys = Object.keys(allCharges) as (keyof Charges)[];
      for (const key of chargeKeys) {
        const found = (allCharges[key] as (Charge | FreelanceCharge)[]).find((c) => c.id === charge.variableChargeId);
        if (found) {
          const baseVal = found.mode === 'uniform' ? found.montantUniforme : (found.montantsMensuels[mi] ?? 0);
          const baseFinMois = (found as Charge).finMois ?? null;
          if (baseFinMois != null && mi > baseFinMois) return 0;
          return baseVal * (pct / 100);
        }
      }
    }
  }

  return charge.mode === 'uniform' ? charge.montantUniforme : (charge.montantsMensuels[mi] ?? 0);
}

/**
 * Applique le coefficient de scénario CA (non destructif).
 * meta.caScenarioPct = 100 → valeur inchangée.
 * meta.caScenarioPct = 80  → valeur × 0.80.
 * Fallback à 100 % si le champ est absent (rétrocompatibilité anciens scénarios).
 */
export function applyCAScenario(state: AppState, value: number): number {
  const pct = state.meta.caScenarioPct;
  if (pct == null || !Number.isFinite(pct)) return value;
  return value * (pct / 100);
}

export function getCA(state: AppState, mi: number): number {
  const base = state.revenus.reduce((sum, r) => {
    if (r.mode === 'uniform') {
      return sum + (mi >= r.debutMois ? r.montantUniforme : 0);
    }
    return sum + (r.montantsMensuels[mi] ?? 0);
  }, 0);
  return applyCAScenario(state, base);
}

export function getCapex(state: AppState, mi: number): number {
  return (state.investissements ?? [])
    .filter((inv) => inv.moisCible === mi)
    .reduce((sum, inv) => sum + inv.montant, 0);
}

export function getChargesOpexRaw(state: AppState, mi: number): number {
  const cats: (keyof typeof state.charges)[] = ['marketing', 'outils', 'tech', 'banque', 'legal', 'locaux', 'vehicules', 'autres'];
  const pct = state.meta.caScenarioPct;
  const caFactor = pct == null || !Number.isFinite(pct) ? 1 : pct / 100;
  const exRanges = getExRangesFromState(state);
  let total = 0;
  for (const cat of cats) {
    for (const c of state.charges[cat] as (Charge | FreelanceCharge)[]) {
      total += getChargeMonthValue(c, mi, state.revenus, state.charges, caFactor, exRanges);
    }
  }
  for (const f of state.charges.freelances) {
    total += getChargeMonthValue(f, mi, state.revenus, state.charges, caFactor, exRanges);
  }
  return total;
}

export function getChargesOpex(state: AppState, mi: number): number {
  let total = getChargesOpexRaw(state, mi);
  const ex = getMoisEx(state, mi);
  // CFE pro-rated monthly
  total += (state.fiscalite.cfe[ex] ?? 0) / 12;
  // other taxes
  for (const t of state.fiscalite.autresTaxes) {
    if (t.type === 'mensuel') total += t.montant;
    else total += t.montant / 12;
  }
  // Sous-traitance notaires partenaires (coût variable par commande). Les notaires salariés
  // passent par la masse salariale (via des Salarie virtuels injectés dans state.salaries).
  total += getNotairesSousTraitanceMois(state, mi);
  return total;
}

export function getSalarieMensuel(state: AppState, mi: number): { brut: number; patronal: number; total: number } {
  let brut = 0;
  let patronal = 0;
  for (const s of state.salaries) {
    const fin = s.moisSortie ?? Infinity;
    if (mi >= s.moisEntree && mi <= fin) {
      const b = s.brut + (s.primes[mi] ?? 0);
      const taux = s.tauxPatronal === 'custom' ? s.customTaux / 100 : parseFloat(s.tauxPatronal);
      brut += b;
      patronal += b * taux;
    }
  }
  return { brut, patronal, total: brut + patronal };
}

export function getSalarieLineMensuel(s: AppState['salaries'][0], mi: number): { brut: number; patronal: number; total: number } {
  const fin = s.moisSortie ?? Infinity;
  if (mi < s.moisEntree || mi > fin) return { brut: 0, patronal: 0, total: 0 };
  const b = s.brut + (s.primes[mi] ?? 0);
  const taux = s.tauxPatronal === 'custom' ? s.customTaux / 100 : parseFloat(s.tauxPatronal);
  const p = b * taux;
  return { brut: b, patronal: p, total: b + p };
}

export function getDirigeantLineMensuel(d: AppState['dirigeants'][0], mi: number): number {
  const remu = d.remuMode ? (d.remuMensuels[mi] ?? 0) : d.remuUniform;
  const taux = d.customTaux / 100;
  return remu * (1 + taux);
}

export function getDirigeantMensuel(state: AppState, mi: number): number {
  return state.dirigeants.reduce((sum, d) => sum + getDirigeantLineMensuel(d, mi), 0);
}

export function getMasseSalariale(state: AppState, mi: number): number {
  return getSalarieMensuel(state, mi).total + getDirigeantMensuel(state, mi);
}

export function getEBE(state: AppState, mi: number): number {
  return getCA(state, mi) - getChargesOpex(state, mi) - getMasseSalariale(state, mi);
}

export function getResultatAvantIS(state: AppState, ex: number): number {
  const [start, end] = getExRangesFromState(state)[ex] ?? [0, 11];
  let total = 0;
  for (let mi = start; mi <= end; mi++) {
    total += getEBE(state, mi) - getCapex(state, mi);
  }
  return total;
}

export function getIS(state: AppState, ex: number): number {
  const rai = getResultatAvantIS(state, ex);
  if (rai <= 0) return 0;
  const tauxNormal = (state.meta.tauxIS ?? 25) / 100;
  const tauxReduit = (state.meta.tauxISReduit ?? 15) / 100;
  const seuil = state.meta.seuilISReduit ?? 42500;
  if (state.fiscalite.tauxReduitPME) {
    const t1 = Math.min(rai, seuil) * tauxReduit;
    const t2 = Math.max(0, rai - seuil) * tauxNormal;
    return t1 + t2;
  }
  return rai * tauxNormal;
}

export function getResultatNet(state: AppState, ex: number): number {
  return getResultatAvantIS(state, ex) - getIS(state, ex);
}

/**
 * Retourne le montant d'IS décaissé au mois mi.
 *
 * Règles fiscales françaises appliquées :
 * - Exercice 1 (création société) : DISPENSE automatique d'acomptes → 0 €
 * - Exercices suivants : 4 acomptes trimestriels calculés sur l'IS de l'exercice N-1
 *   (les acomptes de l'exercice N paient en fait l'IS généré pendant l'exercice N-1).
 * - Pour les exercices < 4 mois, versements répartis sur les mois disponibles.
 *
 * Conséquence pour un prévisionnel :
 * - Ex1 : aucun décaissement IS (même si RAI > 0, l'IS ex1 sera payé pendant ex2)
 * - Ex2 : 4 acomptes trimestriels totalisant IS(ex1)
 * - Ex3 : 4 acomptes trimestriels totalisant IS(ex2)
 * - Etc.
 */
export function getISPaiement(state: AppState, mi: number): number {
  const ex = getMoisEx(state, mi);
  // Exercice 1 : dispense d'acomptes pour les sociétés nouvellement créées
  if (ex === 0) return 0;

  const ranges = getExRangesFromState(state);
  const [start, end] = ranges[ex] ?? [0, 11];
  // L'IS décaissé pendant l'exercice N correspond à l'IS généré pendant N-1
  const isTotal = getIS(state, ex - 1);
  if (isTotal <= 0) return 0;

  const exLen = end - start + 1;
  // Calcul des mois de versement : jusqu'à 4 acomptes, espacés régulièrement
  const nInstallments = Math.min(4, exLen);
  const installmentSet = new Set<number>();
  for (let k = 1; k <= nInstallments; k++) {
    const m = start + Math.round((k / nInstallments) * exLen) - 1;
    installmentSet.add(Math.min(m, end));
  }
  return installmentSet.has(mi) ? isTotal / installmentSet.size : 0;
}

export function getFluxMensuel(state: AppState, mi: number): number {
  return getCA(state, mi)
    - getChargesOpex(state, mi)
    - getCapex(state, mi)
    - getMasseSalariale(state, mi)
    - getISPaiement(state, mi);
}

export function getTresorerieCumulee(state: AppState, mi: number): number {
  let total = state.meta.tresoInitiale;
  for (let i = 0; i <= mi; i++) {
    total += getFluxMensuel(state, i);
  }
  return total;
}

export function getMargeNette(state: AppState, mi: number): number {
  const ca = getCA(state, mi);
  if (ca === 0) return 0;
  const ex = getMoisEx(state, mi);
  const [start, end] = getExRangesFromState(state)[ex] ?? [0, 11];
  const exLen = end - start + 1;
  const rnMensuel = getResultatNet(state, ex) / exLen;
  return (rnMensuel / ca) * 100;
}

export function getPointMort(state: AppState, ex: number): number {
  const [start, end] = getExRangesFromState(state)[ex] ?? [0, 11];
  const exLen = end - start + 1;
  let totalCA = 0;
  let totalCharges = 0;
  let totalMS = 0;
  for (let mi = start; mi <= end; mi++) {
    totalCA += getCA(state, mi);
    totalCharges += getChargesOpex(state, mi) + getCapex(state, mi);
    totalMS += getMasseSalariale(state, mi);
  }
  const totalFixed = totalCharges + totalMS;
  if (totalCA === 0) return 0;
  const margeTaux = (totalCA - totalFixed) / totalCA;
  if (margeTaux <= 0) return totalFixed;
  return totalFixed / margeTaux / exLen;
}

export function getRunway(state: AppState): number {
  const totalMi = state?.meta
    ? getTotalMonths(state.meta.exercice1Duree, state.meta.exercice2Duree, state.meta.exercice3Duree)
    : 36;
  const lastMi = totalMi - 1;
  const lastTreso = getTresorerieCumulee(state, lastMi);
  if (lastTreso >= 0) return 999;

  const windowStart = Math.max(0, lastMi - 5);
  let totalBurn = 0;
  let burnMonths = 0;
  for (let mi = windowStart; mi <= lastMi; mi++) {
    const flux = getFluxMensuel(state, mi);
    if (flux < 0) { totalBurn += Math.abs(flux); burnMonths++; }
  }
  if (burnMonths === 0) return 999;
  const avgBurn = totalBurn / burnMonths;
  const currentTreso = getTresorerieCumulee(state, lastMi);
  if (avgBurn <= 0) return 999;
  return Math.max(0, currentTreso / avgBurn);
}

export function getBudgetPub(state: AppState, months: number[]): number {
  let total = 0;
  const pubKeywords = ['google', 'meta', 'tiktok', 'ads', 'pub', 'display', 'gads'];
  for (const c of state.charges.marketing) {
    const isPub = pubKeywords.some(k => c.libelle.toLowerCase().includes(k));
    if (isPub) {
      for (const mi of months) {
        total += c.mode === 'uniform' ? c.montantUniforme : (c.montantsMensuels[mi] ?? 0);
      }
    }
  }
  return total;
}

export function getROAS(state: AppState, months: number[]): number | null {
  const budget = getBudgetPub(state, months);
  if (budget === 0) return null;
  const totalCA = months.reduce((sum, mi) => sum + getCA(state, mi), 0);
  return totalCA / budget;
}

export function getCAExercice(state: AppState, ex: number): number {
  const [start, end] = getExRangesFromState(state)[ex] ?? [0, 11];
  let total = 0;
  for (let mi = start; mi <= end; mi++) total += getCA(state, mi);
  return total;
}

export function computeAll(state: AppState): CalcResult {
  const totalMi = state?.meta
    ? getTotalMonths(state.meta.exercice1Duree, state.meta.exercice2Duree, state.meta.exercice3Duree)
    : 36;
  const monthly: MonthlyCalc[] = [];
  for (let mi = 0; mi < totalMi; mi++) {
    monthly.push({
      mi,
      ca: getCA(state, mi),
      chargesOpex: getChargesOpex(state, mi),
      capex: getCapex(state, mi),
      salarie: getSalarieMensuel(state, mi),
      dirigeantCout: getDirigeantMensuel(state, mi),
      masseSalariale: getMasseSalariale(state, mi),
      sousTraitance: getNotairesSousTraitanceMois(state, mi),
      ebe: getEBE(state, mi),
      isPaiement: getISPaiement(state, mi),
      flux: getFluxMensuel(state, mi),
      tresorerie: getTresorerieCumulee(state, mi),
      margeNette: getMargeNette(state, mi),
    });
  }
  const exRanges = getExRangesFromState(state);
  const yearly: YearlyCalc[] = [];
  for (let ex = 0; ex < exRanges.length; ex++) {
    const [start, end] = exRanges[ex];
    let ca = 0, chargesOpex = 0, capex = 0, masseSalariale = 0;
    for (let mi = start; mi <= end; mi++) {
      ca += getCA(state, mi);
      chargesOpex += getChargesOpex(state, mi);
      capex += getCapex(state, mi);
      masseSalariale += getMasseSalariale(state, mi);
    }
    const ebe = ca - chargesOpex - masseSalariale;
    const resultatAvantIS = getResultatAvantIS(state, ex);
    const is = getIS(state, ex);
    yearly.push({
      ex,
      ca,
      chargesOpex,
      capex,
      masseSalariale,
      ebe,
      resultatAvantIS,
      is,
      resultatNet: resultatAvantIS - is,
      pointMort: getPointMort(state, ex),
    });
  }
  return { monthly, yearly, runway: getRunway(state) };
}
